import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { UserRegistrationUser } from '../../redux/user/user_auth/user_authServiceSync';
import { InfinitySpin } from 'react-loader-spinner';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const baseSchema = Yup.object({
  name: Yup.string().required(),
  phone_number: Yup.string()
    .required('Phone number is required')
    .matches(/^[0-9]+$/, 'Phone number must contain only digits')
    .min(10, 'Phone number must be at least 10 digits'),
  email: Yup.string().email().required(),
  password: Yup.string().min(6).required(),
  confirm_password: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Confirm Password is required'),
});

const Signup = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { loading } = useSelector((state) => state.user_auth);

  const handleSubmited = async (value, { resetForm }) => {
    const payload = {
      ...value,
      navigate,
    };

    const result = await dispatch(UserRegistrationUser({ ...payload }));

    if (result.payload) {
      resetForm();
      navigate('/auth/signin');
    }
  };

  const { handleChange, handleSubmit, errors, values } = useFormik({
    initialValues: {
      name: '',
      phone_number: '',
      email: '',
      password: '',
      confirm_password: '',
    },
    validationSchema: baseSchema,
    onSubmit: handleSubmited,
  });

  return (
    <>
      <div class="d-flex flex-column h-100 p-3">
        <div class="d-flex flex-column flex-grow-1">
          <div class="row h-100">
            <div class="col-xxl-7">
              <div class="row justify-content-center h-100">
                <div class="col-lg-6 py-lg-5">
                  <div class="d-flex flex-column h-100 justify-content-center">
                    <div class="auth-logo mb-4">
                      <a href="index.html">
                        <img
                          src="/assets/img/logo/glorious_logo.webp"
                          height="24"
                          alt="logo dark"
                          className="h-[3.875rem]"
                        />
                      </a>
                    </div>

                    <h2 class="fw-bold fs-24">Sign Up</h2>

                    <p class="text-muted mt-1 mb-4">
                      New to our platform? Sign up now! It only takes a minute
                    </p>

                    <div>
                      <form className="authentication-form">
                        <div className="mb-3">
                          <label className="form-label" htmlFor="name">
                            Name
                          </label>
                          <input
                            value={values.name || ''}
                            type="text"
                            id="name"
                            name="name"
                            className={`form-control ${
                              errors.name ? 'is-invalid' : ''
                            }`}
                            placeholder="Enter your name"
                            onChange={handleChange}
                            required
                          />
                          {errors.name && (
                            <div className="invalid-feedback">
                              {errors.name}
                            </div>
                          )}
                        </div>

                        <div className="mb-3">
                          <label className="form-label" htmlFor="phone_number">
                            Phone Number
                          </label>
                          <input
                            value={values.phone_number || ''}
                            type="tel"
                            id="phone_number"
                            name="phone_number"
                            className={`form-control ${
                              errors.phone_number ? 'is-invalid' : ''
                            }`}
                            placeholder="Enter your phone number"
                            onChange={handleChange}
                            required
                          />
                          {errors.phone_number && (
                            <div className="invalid-feedback">
                              {errors.phone_number}
                            </div>
                          )}
                        </div>

                        <div className="mb-3">
                          <label className="form-label" htmlFor="email">
                            Email
                          </label>
                          <input
                            value={values.email || ''}
                            type="email"
                            id="email"
                            name="email"
                            className={`form-control ${
                              errors.email ? 'is-invalid' : ''
                            }`}
                            placeholder="Enter your email"
                            onChange={handleChange}
                            required
                          />
                          {errors.email && (
                            <div className="invalid-feedback">
                              {errors.email}
                            </div>
                          )}
                        </div>

                        <div className="mb-3 relative">
                          <label
                            className="form-label text-lg"
                            htmlFor="password"
                          >
                            Password
                          </label>
                          <div className="relative">
                            <input
                              value={values.password || ''}
                              type={showPassword ? 'text' : 'password'}
                              id="password"
                              name="password"
                              className={`form-control ${
                                errors.password ? 'is-invalid' : ''
                              }`}
                              placeholder="Enter your password"
                              onChange={handleChange}
                              required
                            />
                            <span
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute top-50 end-0 translate-middle-y pe-3"
                              style={{ cursor: 'pointer' }}
                            >
                              {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </span>
                          </div>
                          {errors.password && (
                            <div className="invalid-feedback">
                              {errors.password}
                            </div>
                          )}
                        </div>

                        <div className="mb-3 relative">
                          <label
                            className="form-label text-lg"
                            htmlFor="confirm_password"
                          >
                            Confirm Password
                          </label>
                          <div className="relative">
                            <input
                              value={values.confirm_password || ''}
                              type={showConfirmPassword ? 'text' : 'password'}
                              id="confirm_password"
                              name="confirm_password"
                              className={`form-control ${
                                errors.confirm_password ? 'is-invalid' : ''
                              }`}
                              placeholder="Re-enter your password"
                              onChange={handleChange}
                              required
                            />
                            <span
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              className="absolute top-50 end-0 translate-middle-y pe-3"
                              style={{ cursor: 'pointer' }}
                            >
                              {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                            </span>
                          </div>
                          {errors.confirm_password && (
                            <div className="invalid-feedback">
                              {errors.confirm_password}
                            </div>
                          )}
                        </div>

                        <div className="mb-1 text-center d-grid">
                          <button
                            className="btn btn-soft-primary"
                            type="button"
                            onClick={handleSubmit}
                          >
                            {loading ? (
                              <div className="w-full flex items-center justify-center">
                                <InfinitySpin width="80" color="#F61B1C" />
                              </div>
                            ) : (
                              <>Sign Up</>
                            )}
                          </button>
                        </div>
                      </form>
                    </div>

                    <p class="mt-auto text-danger text-center">
                      I already have an account{' '}
                      <NavLink to={'/auth/signin'}>
                        <a class="text-dark fw-bold ms-1">Sign In</a>
                      </NavLink>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xxl-5 d-none d-xxl-flex">
              <div class="card h-100 mb-0 overflow-hidden">
                <div class="d-flex flex-column h-100">
                  <img
                    src="/assets/images/small/img-10.jpg"
                    alt=""
                    class="w-100 h-100"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Signup;
